/**
 * Phase 1.7 — /dev/components playground.
 *
 * Visual review surface for every v2 primitive. Renders each one with a
 * couple of representative variants in BOTH light and dark mode side-by-
 * side via two scoped wrappers; this is enough to spot dark-mode
 * regressions without leaving the route.
 *
 * Not auth-guarded — accessible via the AuthGuardV2 wrapper so Brian can
 * QA the chrome with a real session, but the page itself doesn't fetch
 * any user data.
 */

import { Bug, Calendar, FileText, Heart, Plus, Trash2, Wrench } from 'lucide-react';
import { Card, CardThumbnailFallback, EmptyState, KpiCard, FilterBar, SectionHeader, ViewToggle, WelcomeBanner } from '@/components/v2';
import { OnboardingNudge, RailSection } from '@/components/v2/layout';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { usePageHeader } from '@/components/v2/layout/Header';

export function ComponentsPlaygroundPage(): JSX.Element {
  usePageHeader('Component playground');
  return (
    <div className="space-y-12">
      <Section title="Light mode">
        <Showcase mode="light" />
      </Section>
      <Section title="Dark mode (forced via .dark class on this scope)">
        <div className="dark">
          <div className="bg-sand-dark p-6 text-charcoal-dark dark:bg-sand-dark">
            <Showcase mode="dark" />
          </div>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="space-y-4">
      <h2 className="font-display text-2xl">{title}</h2>
      <div className="rounded-xl border border-border bg-surface p-6 dark:bg-surface-dark">
        {children}
      </div>
    </section>
  );
}

function Showcase({ mode }: { mode: 'light' | 'dark' }): JSX.Element {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const id = `playground-${mode}`;

  return (
    <div className="space-y-8">
      <Block label="WelcomeBanner">
        <WelcomeBanner
          name="Brian"
          subtitle="Three tasks need attention this morning."
          actions={
            <Button className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Plus className="h-4 w-4" />
              New task
            </Button>
          }
        />
      </Block>

      <Block label="KpiCard strip">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            label="Animals"
            icon={Bug}
            primary={{ label: 'Active', value: 142 }}
            secondary={{ label: 'Sold/Deceased', value: 7, tone: 'warning' }}
          />
          <KpiCard
            label="Vehicles"
            icon={Wrench}
            primary={{ label: 'In Service', value: 12 }}
            secondary={{ label: 'Maintenance', value: 2, tone: 'warning' }}
            trend={{ delta_pct: 4.2, direction: 'up' }}
          />
          <KpiCard
            label="Inventory"
            primary={{ label: 'In Stock', value: 318 }}
            secondary={{ label: 'Below Threshold', value: 11, tone: 'warning' }}
          />
          <KpiCard
            label="Tasks"
            icon={Calendar}
            primary={{ label: 'Complete', value: 23, tone: 'positive' }}
            secondary={{ label: 'Incomplete', value: 4 }}
            hint="Week of Apr 27"
          />
        </div>
      </Block>

      <Block label="FilterBar">
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: 'Search animals' }}
          filters={[
            <Button key="species" variant="outline" size="sm">All species</Button>,
            <Button key="pasture" variant="outline" size="sm">All pastures</Button>,
            <Button key="status" variant="outline" size="sm">All statuses</Button>,
          ]}
          onClear={() => setSearch('')}
          hasActiveFilters={Boolean(search)}
          actions={
            <>
              <ViewToggle value={view} onChange={setView} />
              <Button className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
                <Plus className="h-4 w-4" />
                Add animal
              </Button>
            </>
          }
        />
      </Block>

      <Block label="SectionHeader + Card grid">
        <SectionHeader title="Cattle" count={3} collapsible defaultOpen>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {['Bessie', 'Daisy', 'Maggie'].map((name) => (
              <Card
                key={name}
                interactive
                onClick={() => undefined}
                thumbnail={<CardThumbnailFallback />}
                title={name}
                subtitle="Heifer · DOB 2023-06-12"
                badge={
                  <span className="rounded-full bg-moss/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-moss dark:bg-moss-dark/20 dark:text-moss-dark">
                    Active
                  </span>
                }
                footer={
                  <div className="flex items-center justify-between">
                    <span className="text-burnt-orange dark:text-burnt-orange-dark">Open</span>
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                }
              />
            ))}
          </div>
        </SectionHeader>
      </Block>

      <Block label="EmptyState — page variant">
        <EmptyState
          icon={Bug}
          title="No animals yet"
          description="Add your first animal to start tracking weights, health events, and pasture rotations."
          action={<Button>Add animal</Button>}
        />
      </Block>

      <Block label="EmptyState — card variant">
        <Card title="Recent vet visits">
          <EmptyState
            variant="card"
            icon={Heart}
            title="No vet visits this month"
            description="Log a visit from the QuickLog menu."
          />
        </Card>
      </Block>

      <Block label="OnboardingNudge">
        <OnboardingNudge
          id={`${id}-onboarding`}
          title="Connect your first calendar"
          description="Sync recurring tasks to your phone or laptop."
          action={<Button size="sm" variant="outline">Connect</Button>}
        />
      </Block>

      <Block label="RailSection (excerpts of right rail content)">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <RailSection title="Today's tasks" action={<a href="#" className="text-xs text-burnt-orange dark:text-burnt-orange-dark">View all →</a>}>
            <ul className="space-y-2 text-sm">
              <li>Move cattle to north pasture</li>
              <li>Check fence at the creek</li>
              <li>Feed the layers</li>
            </ul>
          </RailSection>
          <RailSection title="Linked records">
            <ul className="space-y-2 text-sm">
              <li><FileText className="mr-2 inline h-4 w-4" />Vet invoice #4421</li>
              <li><FileText className="mr-2 inline h-4 w-4" />Bill of sale</li>
            </ul>
          </RailSection>
        </div>
      </Block>
    </div>
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="space-y-2">
      <p className="font-mono text-2xs uppercase tracking-wider text-muted-foreground">{label}</p>
      {children}
    </div>
  );
}
